// Data seam for the password-recovery flow. Holds the password
// policy (shared with the set-password screen) and the two async actions the
// flow performs.
//
// Wired:
//   requestPasswordReset -> POST /auth/password/forgot  { email }  (always 200,
//     no account enumeration — the "revisa tu correo" screen is identical either way)
//   submitNewPassword    -> POST /auth/password/reset   { token, password }
//     Resolves -> "Contraseña actualizada" (server revokes all other sessions);
//     rejects (400) on an expired/used token. NOTE: the page only renders the
//     "enlace caducado" state from the EXPIRED_TOKEN_SENTINEL url today; to show
//     it on a real rejected submit, onSetPassword must catch and setView('expired')
//     (queued to design in handoff-backend-to-design.md). The reset link is
//     single-use and 1h TTL (server-enforced).

// Leaf subpath import, never the @vysto/shared barrel and never ./auth (its
// index pulls jwt.js → jsonwebtoken → browser crash). The leaf resolves to a
// FILE under the vite alias, which is the shared-subpath contract.
import { PASSWORD_RULES, passwordMeetsPolicy } from '@vysto/shared/auth/password-policy'

import { apiClient } from '../../api/client.js'

/** Human-readable lifetime of a reset link, for page copy. The real TTL is server-enforced. */
export const RESET_LINK_TTL = '1 hora'

/** Sentinel token that renders the expired-link state (no backend to validate). */
export const EXPIRED_TOKEN_SENTINEL = 'caducado'

// The policy + rules now live in @vysto/shared (one source for the server gate
// and this meter). Re-exported so existing imports keep working.
export { PASSWORD_RULES, passwordMeetsPolicy }
export type { PasswordRule } from '@vysto/shared/auth/password-policy'

/** POST /auth/password/forgot — always 200 (no account enumeration); the confirmation screen is identical either way. */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post('/auth/password/forgot', { email })
}

/**
 * POST /auth/password/reset — sets the new password (server revokes every
 * other session). Rejects with 400 on an expired/used token.
 */
export async function submitNewPassword(token: string, password: string): Promise<void> {
  await apiClient.post('/auth/password/reset', { token, password })
}
